import React, { Suspense, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import RouteAwareOutlet from './RouteAwareOutlet';
import { SIDEBAR_WIDTH, CONTENT_PADDING } from './layoutClasses';
import './AppLayout.css';
import AppHeader from './AppHeader';
import AppSidebar from './AppSidebar';
import MobileNavigationDrawer from './MobileNavigationDrawer';
import PageLoader, { APP_SURFACE } from './PageLoader';
import { itemsVisibles } from './navConfig';
import { API_URL } from '../../config/api';
import { AuthContext } from '../../context/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { isFeatureEnabled, areRiskyFeaturesEnabled } from '../../config/featureFlags';
import { openIsolatedSession, isIsolatedSession } from '../../firebase';
import { apiFetch } from '../../utils/apiClient';

const AppLayout = () => {
    // 4. Obtenemos el usuario y la función de logout del contexto
    const { currentUser, logout } = useContext(AuthContext);
    const navigate = useNavigate(); // Hook para la redirección

    const [menuOpen, setMenuOpen] = useState(false);
    const menuTriggerRef = useRef(null);
    const closeMenu = useCallback(() => setMenuOpen(false), []);
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia?.('(min-width: 1024px)').matches || false);
    useEffect(() => {
        const media = window.matchMedia?.('(min-width: 1024px)');
        if (!media) return;
        const updateViewport = () => {
            setIsDesktop(media.matches);
            if (media.matches) closeMenu();
        };
        updateViewport();
        media.addEventListener('change', updateViewport);
        return () => media.removeEventListener('change', updateViewport);
    }, [closeMenu]);
    const { toggleTheme, isDark } = useTheme();
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminChecked, setAdminChecked] = useState(false);
    const [userRole, setUserRole] = useState(() => localStorage.getItem('cached_role') || 'user'); // 'user', 'admin', 'taxi_admin'
    const [subscriptionExpired, setSubscriptionExpired] = useState(false);
    const [userPlan, setUserPlan] = useState('trial'); // Plan del usuario (trial, basic, pro, business, enterprise)
    const [canUseMultisession, setCanUseMultisession] = useState(false);
    const [advancedFeaturesEnabled, setAdvancedFeaturesEnabled] = useState(false); // Por defecto desactivado

    const location = useLocation();
    useEffect(() => { closeMenu(); }, [location.pathname, currentUser, closeMenu]);

    // 🛡️ Ref global para cleanup seguro
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => { isMountedRef.current = false; };
    }, []);

    // ✅ FIX #1: Cargar settings con API REST en lugar de Firestore
    const [showRateLimiterBar, setShowRateLimiterBar] = useState(true);
    useEffect(() => {
        let isMounted = true;
        if (!currentUser) return;

        const loadSettings = async () => {
            try {
                // Fetch Global Settings
                const settingsRes = await apiFetch('/api/settings');
                if (!isMounted) return;
                const settingsData = settingsRes.ok ? await settingsRes.json() : null;
                setAdvancedFeaturesEnabled(settingsData?.advancedFeaturesEnabled || false);

                // Fetch Rate Limiter Config
                const rateLimiterRes = await apiFetch('/api/rate-limiter/config');
                if (!isMounted) return;
                const rateLimiterData = rateLimiterRes.ok ? await rateLimiterRes.json() : null;
                
                if (rateLimiterData?.config) {
                    const floatingBarSetting = rateLimiterData.config.showFloatingBar;
                    setShowRateLimiterBar(floatingBarSetting !== false);
                } else {
                    setShowRateLimiterBar(true);
                }
            } catch (error) {
                console.error('Error cargando settings vía API:', error);
            }
        };

        loadSettings();
        return () => {
            isMounted = false;
        };
    }, [currentUser]);

    // 🔄 Listener para actualización instantánea desde RateLimiterSettings
    useEffect(() => {
        const handleVisibilityChange = (event) => {
            if (isMountedRef.current) {
                setShowRateLimiterBar(event.detail.showFloatingBar);
            }
        };

        window.addEventListener('rateLimiterVisibilityChange', handleVisibilityChange);
        return () => {
            window.removeEventListener('rateLimiterVisibilityChange', handleVisibilityChange);
        };
    }, []);

    // Verificación de rol original: no modifica el contrato de acceso.
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        let isMounted = true;

        const checkAdminAccess = async () => {
            if (!currentUser) {
                if (isMounted) {
                    setIsAdmin(false);
                    setAdminChecked(true);
                }
                return;
            }

            try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/admin/check-role`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    cache: 'no-store',
                    signal
                });

                if (response.ok) {
                    const data = await response.json();
                    if (isMounted) {
                        setIsAdmin(!!data.isAdmin);
                        setUserRole(data.role || 'user');
                        localStorage.setItem('cached_role', data.role || 'user');
                    }
                } else {
                    // Fallback: verificar por email si la API falla
                    const adminEmails = ['admin@chatbot.com', 'administrador@edificio.com'];
                    if (isMounted) {
                        setIsAdmin(adminEmails.includes(currentUser.email));
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error verificando permisos de admin:', error);
                    // Fallback: verificar por email si hay error
                    const adminEmails = ['admin@chatbot.com', 'administrador@edificio.com'];
                    if (isMounted) {
                        setIsAdmin(adminEmails.includes(currentUser.email));
                    }
                }
            } finally {
                if (isMounted) {
                    setAdminChecked(true);
                }
            }
        };

        checkAdminAccess();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [currentUser]);

    // CORRECCIÓN 2: Verificar multisession con cleanup
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        let isMounted = true;

        const checkMultisessionAccess = async () => {
            if (!currentUser) {
                if (isMounted) {
                    setCanUseMultisession(false);
                }
                return;
            }

            try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/usage/multisession-access`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    signal
                });

                if (response.ok) {
                    const data = await response.json();
                    if (isMounted) {
                        setCanUseMultisession(data.allowed === true);
                    }
                } else {
                    if (isMounted) {
                        setCanUseMultisession(false);
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error verificando acceso multisesión:', error);
                    if (isMounted) {
                        setCanUseMultisession(false);
                    }
                }
            }
        };

        checkMultisessionAccess();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [currentUser]);

    // CORRECCIÓN 3: Verificar suscripción y feature locking
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        let isMounted = true;

        const checkSubscriptionStatus = async () => {
            if (!currentUser) {
                if (isMounted) {
                    setSubscriptionExpired(false);
                }
                return;
            }

            try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/subscription/status`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    signal
                });

                if (response.ok) {
                    const data = await response.json();
                    const status = data.subscription?.status;
                    // Normalizar planId
                    const plan = (data.subscription?.planId || 'trial').toLowerCase();
                    if (isMounted) {
                        setSubscriptionExpired(status === 'expired');
                        setUserPlan(plan);
                    }
                } else {
                    if (isMounted) {
                        setSubscriptionExpired(false);
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error verificando suscripción:', error);
                    if (isMounted) {
                        setSubscriptionExpired(false);
                    }
                }
            }
        };

        checkSubscriptionStatus();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [currentUser]);

    // CORRECCIÓN 4: Event listener separado con protección isMountedRef
    useEffect(() => {
        const handleSubscriptionUpdate = () => {
            if (!isMountedRef.current) return; // ✅ FIX #3: Protección contra memory leak
            // Forzar re-verificación reseteando el estado
            setSubscriptionExpired(false);
        };

        window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);

        return () => {
            window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
        };
    }, []);

    // 5. Creamos la función para manejar el cierre de sesión
    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login'); // Redirigimos al login después de cerrar sesión
        } catch (error) {
            console.error("Error al cerrar sesión:", error);
            // Aquí podrías mostrar una notificación de error al usuario
        }
    };

    const groups = itemsVisibles({ rol: userRole, isAdmin, adminChecked, subscriptionExpired, userPlan,
        flags: { advancedFeaturesEnabled: Boolean(advancedFeaturesEnabled), AI_ASSISTANT: isFeatureEnabled('AI_ASSISTANT'),
            LIVE_CHAT: isFeatureEnabled('LIVE_CHAT'), RISKY_FEATURES: areRiskyFeaturesEnabled() } });
    const accountItems = groups.find(group => group.title === 'Cuenta')?.items || [];
    const isolated = isIsolatedSession();

    return (
        <>
            <div inert={menuOpen} className={`${APP_SURFACE} flex h-[100dvh] min-w-0 flex-col overflow-hidden`}>
                <AppHeader isTaxiAdmin={userRole === 'taxi_admin'} onOpenMenu={() => setMenuOpen(true)} menuOpen={menuOpen}
                    menuTriggerRef={menuTriggerRef} showMenuButton={!isDesktop} isDark={isDark} toggleTheme={toggleTheme} currentUser={currentUser}
                    accountItems={accountItems} userPlan={userPlan} showRateLimiterBar={showRateLimiterBar}
                    isolated={isolated} canOpenSession={!isolated && canUseMultisession && adminChecked && isAdmin}
                    openSession={openIsolatedSession} onLogout={handleLogout} />
                <div className="flex min-h-0 flex-1">
                    <aside aria-label="Navegación principal" className={`hidden ${SIDEBAR_WIDTH} shrink-0 flex-col border-r border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-800 lg:flex`}>
                        <AppSidebar groups={groups} pathname={location.pathname} />
                    </aside>
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                        {subscriptionExpired && (
                            <div className="shrink-0 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-3 shadow-lg sm:px-6 sm:py-4">
                                <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-shrink-0">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="font-semibold">🔒 Tu suscripción ha expirado</p>
                                            <p className="text-sm text-white/90">Renueva tu plan para continuar usando todas las funciones</p>
                                        </div>
                                    </div>
                                    <Link
                                        to="/pricing"
                                        className="flex-shrink-0 bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors shadow-lg"
                                    >
                                        Renovar Ahora
                                    </Link>
                                </div>
                            </div>
                        )}
                        <main className={`app-main ${APP_SURFACE} ${CONTENT_PADDING} relative flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]`}>
                            <Suspense fallback={<PageLoader />}>
                                <RouteAwareOutlet />
                            </Suspense>
                        </main>
                    </div>
                </div>
            </div>
            {menuOpen && !isDesktop && <MobileNavigationDrawer groups={groups} pathname={location.pathname} onClose={closeMenu} triggerRef={menuTriggerRef} />}
        </>
    );
};

export default AppLayout;
